<template>
  <div id="crew-list" class="h-full vx-card p-4">
    <!-- フィルター -->
    <div class="flex justify-between p-4 pt-0 gap-x-4 items-end">
      <div class="flex flex-wrap items-center gap-4">
        <SearchInput
          class="w-48"
          placeholder="船員名"
          v-model="searchCrewName"
        ></SearchInput>
        <SelectInput
          style="width:12rem;"
          v-model="selectedFilterEmploymentStatus"
          :options="employmentStatusFilterOptions"
          :multiple="true"
          placeholder="社員区分"
          :clearable="true"
          :limit="1"
          :limitText="count => `+${count}`"
          noOptionsText="なし"
        ></SelectInput>
        <SearchInput
          class="w-48"
          placeholder="所属企業"
          v-model="searchAffiliatedCompanyName"
        ></SearchInput>
        <SelectInput
          style="width: 12rem;"
          v-model="selectedFilterAppointmentRoleIds"
          :options="crewRoles"
          :multiple="true"
          placeholder="辞令職"
          :limit="1"
          :limitText="count => `+${count}`"
          noOptionsText="なし"
        ></SelectInput>
        <SearchInput
          class="w-48"
          placeholder="社員番号"
          v-model="searchEmployeeNumber"
        ></SearchInput>
        <SelectInput
          class="w-48"
          v-model="selectedFilterOnboardingStatus"
          :options="[
            { id: 'enrolled', label: '在籍中' },
            { id: 'retirement', label: '退職済' },
          ]"
          :multiple="false"
          :clearable="true"
          placeholder="在籍ステータス"
          :limit="1"
          :limitText="count => `+${count}`"
          noOptionsText="なし"
        ></SelectInput>
      </div>
      <div class="flex-shrink-0">
        <Button
          icon="icon-plus"
          color="blue"
          label="新規登録"
          @click="goToAdd"
        />
      </div>
    </div>

    <vs-divider :color="AISEA_COLOR.LIGHT_GRAY" class="my-0" />

    <!-- AgGrid Table -->
    <ag-grid-vue
      ref="agGridTableCrewList"
      :gridOptions="gridOptions"
      class="ag-theme-material w-full ag-grid-table"
      headerHeight="47"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :rowData="filteredCrewList"
      domLayout="autoHeight"
      rowSelection="multiple"
      colResizeDefault="shift"
      :animateRows="true"
      :floatingFilter="false"
      :suppressPaginationPanel="true"
      :suppressDragLeaveHidesColumns="true"
      :suppressRowClickSelection="true"
      :suppressCellFocus="true"
      :suppressNoRowsOverlay="false"
      @grid-ready="onGridReady"
      @rowClicked="rowClicked"
    />

    <!-- 削除確認ダイアログ -->
    <vs-prompt
      :active.sync="showDeleteDialog"
      title="船員削除"
      :color="AISEA_COLOR.RED"
      buttons-hidden
      @close="cancelDelete"
    >
      <div class="delete-dialog-content">
        船員情報を削除してよろしいですか？データは完全に削除され、閲覧できなくなります。
      </div>
      <div class="delete-dialog-buttons flex justify-end gap-3 mt-4">
        <Button
          label="削除"
          type="filled"
          colorType="danger"
          @click="confirmDelete"
        />
        <Button
          label="キャンセル"
          type="filled"
          colorType="cancel"
          @click="cancelDelete"
        />
      </div>
    </vs-prompt>
  </div>
</template>

<script>
import { AgGridVue } from "ag-grid-vue";
import moment from "moment";
import SearchInput from "../components/SearchInput.vue";
import SelectInput from "../components/form/SelectInput.vue";
import "@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss";
import _ from "lodash";
import CellRendererActions from "./cell-renderer/CellRendererActions.vue";
import { DeviceInfoMixin, ColorMixin } from "@/mixins";
import {
  EnterpriseCrewsService,
  EnterpriseCrewRolesService,
} from "../service/CrewBasicInfoService.js";
import Button from "../components/Button.vue";
import {
  EmploymentStatusOptionsForTree,
  isPermanentEmployee,
} from "../constants/EmploymentStatus.js";
import { mapState } from "vuex";

export default {
  name: "CrewList",
  mixins: [DeviceInfoMixin, ColorMixin],
  components: {
    AgGridVue,
    CellRendererActions,
    SearchInput,
    Button,
    SelectInput,
  },
  data() {
    return {
      crewList: [],
      crewRoles: [],

      // AgGrid
      gridApi: null,
      gridOptions: {
        localeText: {
          noRowsToShow:
            '<div id="no-rows-crew-list" class="aisea-text-16-400-black">表示する船員基本情報がありません</div>',
        },
        context: {
          componentParent: this,
        },
      },
      defaultColDef: {
        sortable: false,
        resizable: true,
        suppressMenu: true,
        icons: {
          sortAscending:
            '<span class="material-icons">keyboard_arrow_up</span>',
          sortDescending:
            '<span class="material-icons">keyboard_arrow_down</span>',
          sortUnSort: '<span class="material-icons">unfold_more</span>',
        },
        unSortIcon: true,
        comparator: () => {
          return;
        },
      },
      columnDefs: [],

      // Filter
      searchCrewName: "",
      selectedFilterEmploymentStatus: [],
      selectedFilterAppointmentRoleIds: [],
      searchAffiliatedCompanyName: "",
      searchEmployeeNumber: "",
      selectedFilterOnboardingStatus: null,

      // Delete Dialog
      showDeleteDialog: false,
      selectedCrewForDelete: null,
    };
  },
  computed: {
    ...mapState("auth", {
      user: state => state.user,
    }),
    employmentStatusFilterOptions() {
      return EmploymentStatusOptionsForTree;
    },
    filteredCrewList() {
      return this.crewList.filter(crew => {
        const name = crew.name ? crew.name : "";
        const employeeNumber = crew.employee_number ? crew.employee_number : "";
        const affiliatedCompanyName = crew.affiliated_company_name
          ? crew.affiliated_company_name
          : "";
        const roleId = crew.role_id ? crew.role_id : "";

        // 選択肢が未選択の場合はフィルターしない
        const roleFilterBool =
          this.selectedFilterAppointmentRoleIds.length > 0
            ? this.selectedFilterAppointmentRoleIds.includes(roleId)
            : true;

        // 社員区分フィルター
        const employmentStatusFilterBool =
          this.selectedFilterEmploymentStatus.length > 0
            ? this.selectedFilterEmploymentStatus.includes(
                crew.affiliated_company_status
              )
            : true;

        // 在籍ステータスフィルター
        // retirement_dateがnullの場合は在籍中扱い
        // retirement_dateが存在し、現在日時より前なら退職済み、それ以外は在籍中
        let crewOnboardingStatus = "enrolled"; // デフォルトは在籍中
        if (crew.retirement_date) {
          const retirementDate = moment(crew.retirement_date);
          if (retirementDate.isValid() && retirementDate.isBefore(moment())) {
            crewOnboardingStatus = "retirement";
          }
        }
        const onboardingStatusFilterBool = this.selectedFilterOnboardingStatus
          ? this.selectedFilterOnboardingStatus === crewOnboardingStatus
          : true;

        return (
          name.includes(this.searchCrewName) &&
          employeeNumber.includes(this.searchEmployeeNumber) &&
          affiliatedCompanyName.includes(this.searchAffiliatedCompanyName) &&
          roleFilterBool &&
          employmentStatusFilterBool &&
          onboardingStatusFilterBool
        );
      });
    },
  },

  methods: {
    async fetchCrewList() {
      const crewList = await EnterpriseCrewsService.fetch();
      // 所属企業と辞令職が配列になっていてそのままだとフィルターしにくいので、
      // Propertiesに単純にStringに変換したものを追加したCrewListを算出する
      this.crewList = crewList.map(crew => {
        const latestAffiliatedCompany = this.getLatestAffiliatedCompany(
          crew.affiliated_companies
        );

        // 所属企業名を決定
        // 社員区分が「社員」の場合は自社名を設定
        let affiliatedCompanyName = null;
        if (latestAffiliatedCompany) {
          if (isPermanentEmployee(latestAffiliatedCompany.status)) {
            affiliatedCompanyName =
              this.user && this.user.name ? this.user.name : null;
          } else {
            affiliatedCompanyName = latestAffiliatedCompany.name || null;
          }
        }

        return {
          ...crew,
          affiliated_company_name: affiliatedCompanyName,
          affiliated_company_status: latestAffiliatedCompany
            ? latestAffiliatedCompany.status
            : null,
          role_id: this.getLatestRoleId(crew.available_roles),
        };
      });
    },

    async fetchCrewRoles() {
      const crewRoles = await EnterpriseCrewRolesService.fetch();
      this.crewRoles = crewRoles;
    },

    async onGridReady() {
      try {
        this.$vs.loading({ color: this.AISEA_COLOR.BLUE });
        await this.fetchCrewRoles();
        await this.fetchCrewList();
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "エラー",
          text: "船員一覧の取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    rowClicked(e) {
      const crewId = e.data.id;
      this.$router.push({
        name: "enterprise-management-crew-settings-info",
        params: { id: crewId },
      });
    },

    goToAdd() {
      this.$router.push({ name: "enterprise-management-crew-settings-add" });
    },

    getLatestAffiliatedCompany(affiliatedCompanies) {
      const company = affiliatedCompanies.sort((a, b) => {
        return new Date(b.start_date) - new Date(a.start_date);
      })[0];
      return company;
    },

    getLatestRoleId(availableRoles) {
      const roles = availableRoles.sort((a, b) => {
        return new Date(b.start_date) - new Date(a.start_date);
      });
      const roleId = roles.length > 0 ? roles[0].role_id : null;
      if (!roleId) {
        return "-";
      }

      return roleId;
    },
    async confirmDelete() {
      if (!this.selectedCrewForDelete) {
        return;
      }
      try {
        await EnterpriseCrewsService.deleteById(this.selectedCrewForDelete.id);
        this.$vs.notify({
          title: "船員削除",
          text: "船員を削除しました",
          color: "success",
          iconPack: "feather",
          icon: "icon-check",
        });
        this.fetchCrewList();
      } catch (error) {
        this.$vs.notify({
          title: "エラー",
          text: "船員の削除に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        this.selectedCrewForDelete = null;
        this.showDeleteDialog = false;
      }
    },
    cancelDelete() {
      this.selectedCrewForDelete = null;
      this.showDeleteDialog = false;
    },
  },
  async beforeMount() {
    const initialColumnDefs = [
      {
        headerName: "氏名",
        field: "name",
        minWidth: 200,
        flex: 1,
      },
      {
        headerName: "社員区分",
        minWidth: 120,
        flex: 1,
        field: "affiliated_company_status",
        valueFormatter: params => {
          if (!params.value) {
            return "-";
          }
          return this.employmentStatusFilterOptions.find(
            option => option.id === params.value
          ).label;
        },
      },
      {
        headerName: "所属企業",
        minWidth: 180,
        flex: 1,
        field: "affiliated_company_name",
        valueFormatter: params => {
          return params.value ? params.value : "-";
        },
      },
      {
        headerName: "辞令職",
        minWidth: 200,
        flex: 1,
        field: "role_id",
        valueFormatter: params => {
          const role = this.crewRoles.find(role => role.id === params.value);
          if (!role) {
            return "-";
          }
          return role.label;
        },
      },
      // 入社日
      {
        headerName: "入社日",
        minWidth: 120,
        flex: 1,
        field: "joining_date",
        valueFormatter: params => {
          return params.value
            ? moment(params.value).format("YYYY/MM/DD")
            : "----/--/--";
        },
      },
      // 社員番号
      {
        headerName: "社員番号",
        minWidth: 120,
        flex: 1,
        field: "employee_number",
        valueFormatter: params => {
          return params.value ? params.value : "-";
        },
      },
      // 組合員
      {
        headerName: "組合員該当",
        minWidth: 120,
        flex: 1,
        field: "union_member",
        valueFormatter: params => {
          return params.value ? "組合員" : "-";
        },
      },
      {
        headerName: "メールアドレス",
        field: "email_address",
        minWidth: 240,
        sortable: false,
        flex: 1,
        valueFormatter: params => {
          return params.value ? params.value : "-";
        },
      },
      // 退職日
      {
        headerName: "退職日",
        minWidth: 120,
        flex: 1,
        field: "retirement_date",
        valueFormatter: params => {
          return params.value
            ? moment(params.value).format("YYYY/MM/DD")
            : "----/--/--";
        },
      },
      // 備考
      {
        headerName: "備考",
        minWidth: 200,
        flex: 1,
        sortable: false,
        field: "memo",
        valueFormatter: params => {
          return params.value ? params.value : "-";
        },
      },

      // 操作
      {
        headerName: "操作",
        field: "action",
        minWidth: 50,
        maxWidth: 50,
        cellRenderer: "CellRendererActions",
        pinned: "right",
        resizable: false,
        sortable: false,
        suppressMenu: true,
        cellRendererParams: {
          delete: data => {
            this.selectedCrewForDelete = data;
            this.showDeleteDialog = true;
          },
        },
      },
    ];
    this.columnDefs = initialColumnDefs;
  },
  mounted() {
    let routerViewElement = document.getElementsByClassName("router-view");
    routerViewElement[0].style.padding = "2.2rem";

    this.gridApi = this.gridOptions.api;
    this.gridColumnApi = this.gridOptions.columnApi;
  },
};
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

#crew-list {
  .ag-grid-table {
    height: 100%;

    font-size: 14px;
    font-weight: 400;

    .ag-header-cell-text {
      font-size: 12px;
      font-weight: 600;
    }
    .ag-header-cell {
      padding-right: 0;
    }

    .ag-cell {
      padding-left: 10px;
      padding-right: 10px;
    }
    .ag-header-cell {
      padding-left: 10px;
      padding-right: 10px;
    }
  }
  .ag-header-cell {
    padding-right: 0;
  }
  .vs-tabs--content {
    padding: 4px 0 0 0;
  }
}

.delete-dialog-content {
  font-size: 14px;
  line-height: 1.6;
  color: $aisea-dark-gray;
  padding: 1rem 0;
}
</style>
